import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Tag, Store, Package, Calendar, DollarSign, Percent,
  Save, X, Loader, AlertCircle
} from 'lucide-react';
import adminService, {
  UpdateOfferRequest,
  OfferType,
  Category,
  Business,
  Offer
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const EditOffer: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState<UpdateOfferRequest>({
    offer_id: 0,
    business_id: null,
    category_id: null,
    title: '',
    description: '',
    offer_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: null,
    max_discount_amount: null,
    valid_from: '',
    valid_until: '',
    usage_limit: null,
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (offerId) {
      fetchOfferDetails();
      fetchCategories();
      fetchBusinesses();
    }
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOfferById(Number(offerId));
      setOffer(data);
      
      // Pre-populate form
      const validFrom = new Date(data.valid_from);
      const validUntil = new Date(data.valid_until);
      
      setFormData({
        offer_id: data.offer_id,
        business_id: data.business_id,
        category_id: data.category_id,
        title: data.title,
        description: data.description || '',
        offer_type: data.offer_type,
        discount_value: parseFloat(data.discount_value),
        min_purchase_amount: data.min_purchase_amount ? parseFloat(data.min_purchase_amount) : null,
        max_discount_amount: data.max_discount_amount ? parseFloat(data.max_discount_amount) : null,
        valid_from: validFrom.toISOString().slice(0, 16),
        valid_until: validUntil.toISOString().slice(0, 16),
        usage_limit: data.usage_limit,
        is_active: data.is_active === 1,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch offer details');
      navigate('/admin/offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await adminService.getAllCategories();
      setCategories(data.filter(c => c.is_active === 1));
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const data = await adminService.getAllBusinesses({ limit: 10000 });
      setBusinesses(data.businesses.filter(b => b.status === 'approved' || b.status === 'active'));
    } catch (error: any) {
      console.error('Failed to fetch businesses:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.discount_value <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required';
    }

    if (!formData.valid_until) {
      newErrors.valid_until = 'Valid until date is required';
    }

    if (formData.valid_from && formData.valid_until) {
      const fromDate = new Date(formData.valid_from);
      const untilDate = new Date(formData.valid_until);
      if (untilDate <= fromDate) {
        newErrors.valid_until = 'Valid until must be after valid from date';
      }
    }

    if (formData.min_purchase_amount !== null && formData.min_purchase_amount < 0) {
      newErrors.min_purchase_amount = 'Minimum purchase amount cannot be negative';
    }

    if (formData.max_discount_amount !== null && formData.max_discount_amount < 0) {
      newErrors.max_discount_amount = 'Maximum discount amount cannot be negative';
    }

    if (formData.usage_limit !== null && formData.usage_limit < 1) {
      newErrors.usage_limit = 'Usage limit must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setProcessing(true);
      await adminService.updateOffer(formData);
      toast.success('Offer updated successfully!');
      navigate(`/admin/offers/${offerId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update offer');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-20">
        <AlertCircle size={64} className="text-secondary-300 mx-auto mb-4" />
        <p className="text-secondary-600 font-semibold mb-2">Offer not found</p>
        <button
          onClick={() => navigate('/admin/offers')}
          className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
        >
          Back to Offers
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/admin/offers/${offerId}`)}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Edit Offer</h1>
            <p className="text-secondary-600 mt-1">Update offer information</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-soft p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <Tag className="text-primary-500" size={24} />
            <span>Basic Information</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Title <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.title ? 'border-danger-500' : 'border-secondary-200'
                }`}
                placeholder="Enter offer title"
              />
              {errors.title && <p className="text-danger-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={4}
                placeholder="Enter offer description"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Offer Type <span className="text-danger-500">*</span>
              </label>
              <select
                value={formData.offer_type}
                onChange={(e) => setFormData({ ...formData, offer_type: e.target.value as OfferType })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed">Fixed Amount Discount</option>
                <option value="buy_one_get_one">Buy One Get One</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Discount Value <span className="text-danger-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {formData.offer_type === 'percentage' ? (
                    <Percent className="text-secondary-400" size={20} />
                  ) : (
                    <DollarSign className="text-secondary-400" size={20} />
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.discount_value ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder={formData.offer_type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                />
              </div>
              {errors.discount_value && <p className="text-danger-500 text-sm mt-1">{errors.discount_value}</p>}
            </div>
          </div>
        </div>

        {/* Discount Limits */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <DollarSign className="text-success-500" size={24} />
            <span>Discount Limits</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Minimum Purchase Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="text-secondary-400" size={20} />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.min_purchase_amount || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.min_purchase_amount ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder="Optional"
                />
              </div>
              {errors.min_purchase_amount && <p className="text-danger-500 text-sm mt-1">{errors.min_purchase_amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Maximum Discount Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="text-secondary-400" size={20} />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.max_discount_amount || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_discount_amount: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.max_discount_amount ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder="Optional"
                />
              </div>
              {errors.max_discount_amount && <p className="text-danger-500 text-sm mt-1">{errors.max_discount_amount}</p>}
            </div>
          </div>
        </div>

        {/* Validity & Usage */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <Calendar className="text-info-500" size={24} />
            <span>Validity & Usage</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Valid From <span className="text-danger-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.valid_from ? 'border-danger-500' : 'border-secondary-200'
                }`}
              />
              {errors.valid_from && <p className="text-danger-500 text-sm mt-1">{errors.valid_from}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Valid Until <span className="text-danger-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.valid_until}
                min={formData.valid_from || undefined}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.valid_until ? 'border-danger-500' : 'border-secondary-200'
                }`}
              />
              {errors.valid_until && <p className="text-danger-500 text-sm mt-1">{errors.valid_until}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Usage Limit
              </label>
              <input
                type="number"
                min="1"
                value={formData.usage_limit || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  usage_limit: e.target.value ? parseInt(e.target.value) : null 
                })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.usage_limit ? 'border-danger-500' : 'border-secondary-200'
                }`}
                placeholder="Leave empty for unlimited"
              />
              {errors.usage_limit && <p className="text-danger-500 text-sm mt-1">{errors.usage_limit}</p>}
            </div>
          </div>
        </div>

        {/* Association */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <Store className="text-warning-500" size={24} />
            <span>Association (Optional)</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Business
              </label>
              <select
                value={formData.business_id || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  business_id: e.target.value ? parseInt(e.target.value) : null 
                })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Businesses</option>
                {businesses.map((business) => (
                  <option key={business.business_id} value={business.business_id}>
                    {business.business_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Category
              </label>
              <select
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  category_id: e.target.value ? parseInt(e.target.value) : null 
                })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-5 h-5 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-semibold text-secondary-700">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(`/admin/offers/${offerId}`)}
            className="px-6 py-3 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-colors flex items-center space-x-2"
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={processing}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {processing ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}
            <span>{processing ? 'Updating...' : 'Update Offer'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditOffer;

